//// Application Packages
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { object, string } from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import axios from 'axios'
import {
    translateField,
    matchingEvaluationField,
} from '../../functions/field-functions'
import { extractSubmissionPage } from '../../functions/page-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateAccountData,
    updateAccountValidity,
    updateAccountAuxiliary,
    deleteAccountData,
    selectAccount,
} from '../../reducers/accountSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import TooltipComponent from '../../components/simple-components/tooltip-component'
import CheckUserIDComponent from '../../components/navigation-components/check-user-id-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import ModalComponent from '../../components/conditional-components/modal-component'

//// Carbon Components
import {
    Form,
    TextInput,
    Select,
    SelectItem,
    RadioButtonGroup,
    RadioButton,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants
const successMessage = () => {
    return `
<p>
  <strong>You are almost there!</strong> A temporary password has been issued
  and sent to your email with instructions on how to proceed.
</p>
<br />
<p>
  Check your email
  <strong>Inbox</strong>, <strong>Junk</strong>, <strong>Spam</strong>,
  <strong>Clutter</strong>, and <strong>Trash</strong> folders for your
  temporary password email. This email may have been sent to one of these
  folders depending on the security of your email provider.
</p>
<br />
<p>
  Make sure you add <u>MQAOperations@FLHealth.gov</u> to your trusted/allowed
  email address list in your email account settings to ensure the temporary
  password email does not get sent to your <strong>Junk</strong>,
  <strong>Spam</strong>, <strong>Clutter</strong>, and
  <strong>Trash</strong> folders or get filtered out of your email before it is
  received.
</p>
<br />
<p>
  Click <strong>"Login"</strong> to Log In with your email address and temporary
  password.
</p>
`
}

//////// Component
const RegistrationPage = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})
    const [modalData, setModalData] = useState({})
    const [modalOpen, setModalOpen] = useState(false)

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const digital_name = settingsData?.['page']?.['digital_name']
    const written_name = settingsData?.['page']?.['written_name']
    const submit_route = settingsData?.['page']?.['submit_route']
    const fields = settingsData?.['page']?.['fields']
    const re_captcha =
        accountData?.[digital_name]?.['auxiliary']?.['re_captcha']

    //////// Search Params

    //////// Methods

    // Orchestrate Registration Data (Integration)
    const orchestrateRegistration = async (accountData) => {
        try {
            if (accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_REGISTRATION}`
                const url = base_url + api_endpoint
                const {
                    FirstName,
                    MiddleName,
                    LastName,
                    Email,
                    SecretQuestion,
                    SecretAnswer,
                    UserID,
                    EmailComm,
                } = accountData

                // Create Request
                if (
                    FirstName &&
                    LastName &&
                    Email &&
                    SecretQuestion &&
                    SecretAnswer &&
                    UserID &&
                    EmailComm
                ) {
                    const { data } = await axios.post(url, {
                        firstname: FirstName,
                        secondname: MiddleName ? MiddleName : '',
                        lastname: LastName,
                        email: Email,
                        secretquestionid: SecretQuestion,
                        secretanswer: SecretAnswer,
                        userid: UserID,
                        email_comm_flag: EmailComm,
                    })
                    if (data) {
                        return data
                    } else {
                        console.log(
                            `Acount Registration: Response Is Undefined`
                        )
                        return null
                    }
                } else {
                    console.log(
                        `Acount Registration: Request Parameters Are Undefined`
                    )
                    return null
                }
            } else {
                console.log(`Acount Registration: Request Data Is Undefined`)
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Registration Error:', error)
            return null
        }
    }

    // Re-Validate Data (Utility)
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateAccountValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    // Validate Data (Check)
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (accountData?.[digital_name]?.auxiliary?.checked) {
                schema.yup = object({
                    FirstName: string()
                        .min(0)
                        .max(16)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    MiddleName: string().min(0).max(40),
                    LastName: string()
                        .min(0)
                        .max(40)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    Email: string()
                        .email()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'Email Addresses Must Match', () =>
                            matchingEvaluationField(
                                submit?.Email,
                                submit?.EmailConfirm
                            )
                        ),
                    EmailConfirm: string()
                        .email()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'Email Addresses Must Match', () =>
                            matchingEvaluationField(
                                submit?.EmailConfirm,
                                submit?.Email
                            )
                        ),
                    SecretQuestion: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    SecretAnswer: string()
                        .min(0)
                        .max(100)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    EmailComm: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    FirstName: false,
                    FirstNameErrMsg: '',
                    MiddleName: false,
                    MiddleNameErrMsg: '',
                    LastName: false,
                    LastNameErrMsg: '',
                    Email: false,
                    EmailErrMsg: '',
                    EmailConfirm: false,
                    EmailConfirmErrMsg: '',
                    SecretQuestion: false,
                    SecretQuestionErrMsg: '',
                    SecretAnswer: false,
                    SecretAnswerErrMsg: '',
                    EmailComm: false,
                    EmailCommErrMsg: '',
                }
            } else {
                schema.yup = object({
                    FirstName: string()
                        .min(0)
                        .max(16)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    MiddleName: string().min(0).max(40),
                    LastName: string()
                        .min(0)
                        .max(40)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    Email: string()
                        .email()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'Email Addresses Must Match', () =>
                            matchingEvaluationField(
                                submit?.Email,
                                submit?.EmailConfirm
                            )
                        ),
                    EmailConfirm: string()
                        .email()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'Email Addresses Must Match', () =>
                            matchingEvaluationField(
                                submit?.EmailConfirm,
                                submit?.Email
                            )
                        ),
                    UserID: string()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    SecretQuestion: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    SecretAnswer: string()
                        .min(0)
                        .max(100)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    EmailComm: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    FirstName: false,
                    FirstNameErrMsg: '',
                    MiddleName: false,
                    MiddleNameErrMsg: '',
                    LastName: false,
                    LastNameErrMsg: '',
                    Email: false,
                    EmailErrMsg: '',
                    EmailConfirm: false,
                    EmailConfirmErrMsg: '',
                    UserID: false,
                    UserIDErrMsg: '',
                    SecretQuestion: false,
                    SecretQuestionErrMsg: '',
                    SecretAnswer: false,
                    SecretAnswerErrMsg: '',
                    EmailComm: false,
                    EmailCommErrMsg: '',
                }
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle ReCaptcha (Method)
    const handleReCaptcha = async (value) => {
        try {
            if (value) {
                dispatch(
                    updateAccountAuxiliary({
                        digital_name: digital_name,
                        auxiliary: {
                            re_captcha: true,
                        },
                    })
                )
            } else {
                dispatch(
                    updateAccountAuxiliary({
                        digital_name: digital_name,
                        auxiliary: {
                            re_captcha: false,
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Handle ReCaptcha Error:', error)
            return null
        }
    }

    // Handle Data + Redact Data (Method)
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Clear Data (Hard Reset)
    const handleClearData = () => {
        //// Perform Browser Effects

        localStorage.clear()
        sessionStorage.clear()

        //// End Perform Browser Effects

        //// Perform Data Clearing

        dispatch(
            updateAccountData({
                digital_name: digital_name,
                values: {
                    FirstName: '',
                    MiddleName: '',
                    LastName: '',
                    Email: '',
                    EmailConfirm: '',
                    UserID: '',
                    SecretQuestion: '',
                    SecretAnswer: '',
                    EmailComm: '',
                },
            })
        )

        dispatch(
            deleteAccountData({
                digital_name: digital_name,
            })
        )

        //// End Data Clearing

        //// Perform Browser Effects

        console.clear()
        window.location.reload()

        //// End Perform Browser Effects
    }

    // Handle Submission (Method)
    const handleSubmit = async () => {
        try {
            console.info('Account Registration: Submitted')

            // Check For Captcha Completion
            if (re_captcha) {
                console.info('Account Registration: Passed ReCaptcha')

                // Extract Account Data From Redux
                const submit = await extractSubmissionPage(
                    digital_name,
                    accountData
                )

                // Validate Account Submission Data
                const valid = await validation(submit)

                // Check If Account Data Is Valid
                if (valid) {
                    console.info('Account Registration: Passed Valid')

                    setLoading(true)

                    // Send Orchestration Request
                    const orchestrate = await orchestrateRegistration(submit)

                    console.info('Account Registration: Passed Orchestrate')

                    if (
                        orchestrate?.email_available &&
                        orchestrate?.userid_available
                    ) {
                        // Check If Orchestration Processed Submission
                        if (
                            orchestrate?.account_registered &&
                            orchestrate?.registration_email_sent
                        ) {
                            console.info('Account Registration: Success')

                            setLoading(false)
                            setModalOpen(true)
                            setModalData({
                                open: true,
                                alert: true,
                                label: 'Account Registration',
                                heading: 'Temporary Password Issued',
                                paragraph: successMessage(),
                                submit_label: 'Login',
                                around_close: true,
                            })
                            setNotification({
                                type: 'inline',
                                visible: false,
                                kind: '',
                                title: '',
                                subtitle: '',
                            })
                        } else if (
                            orchestrate?.account_registered &&
                            !orchestrate?.registration_email_sent
                        ) {
                            console.info('Account Registration: Success')
                            console.info(
                                'Account Registration: Failed To Send Email'
                            )

                            setLoading(false)
                            setNotification({
                                type: 'inline',
                                visible: true,
                                kind: 'error',
                                title: 'Account Registration Failed:',
                                subtitle:
                                    'Failed To Send Email, Please Try Again Or Contact Support',
                            })

                            console.log(
                                'Account Registration Failed: Failed To Send Email, Please Try Again Or Contact Support'
                            )
                        } else {
                            console.info('Account Registration: Failed')

                            setLoading(false)
                            setNotification({
                                type: 'inline',
                                visible: true,
                                kind: 'error',
                                title: 'Account Registration Failed:',
                                subtitle:
                                    'Submission Does Not Meet Account Creation Requirements',
                            })

                            console.log(
                                'Account Registration Failed: Submission Does Not Meet Account Creation Requirements'
                            )
                        }
                    } else if (
                        orchestrate?.userid_available &&
                        !orchestrate?.email_available
                    ) {
                        console.info('Account Registration: Failed')

                        setLoading(false)
                        dispatch(
                            updateAccountValidity({
                                digital_name: digital_name,
                                errors: {
                                    Email: true,
                                    EmailErrMsg: 'Email Address Already In Use',
                                },
                            })
                        )
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Registration Failed:',
                            subtitle: 'Account Email Address Already In Use',
                        })

                        console.log(
                            'Account Registration Failed: Account Email Address Already In Use'
                        )
                    } else if (
                        orchestrate?.email_available &&
                        !orchestrate?.userid_available
                    ) {
                        console.info('Account Registration: Failed')

                        setLoading(false)
                        dispatch(
                            updateAccountValidity({
                                digital_name: digital_name,
                                errors: {
                                    UserID: true,
                                    UserIDErrMsg: 'User Id Already In Use',
                                },
                            })
                        )
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Registration Failed:',
                            subtitle: 'Account User Id Already In Use',
                        })

                        console.log(
                            'Account Registration Failed: Account User Id Already In Use'
                        )
                    } else if (
                        !orchestrate?.email_available &&
                        !orchestrate?.userid_available &&
                        accountData?.[digital_name]?.auxiliary?.checked
                    ) {
                        console.info('Account Registration: Failed')

                        setLoading(false)
                        dispatch(
                            updateAccountValidity({
                                digital_name: digital_name,
                                errors: {
                                    Email: true,
                                    EmailErrMsg: 'Email Address Already In Use',
                                    UserID: true,
                                    UserIDErrMsg: 'User Id Already In Use',
                                },
                            })
                        )
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Registration Failed:',
                            subtitle: 'User Id Already In Use',
                        })

                        console.log(
                            'Account Registration Failed: User Id Already In Use'
                        )
                    } else if (
                        !orchestrate?.email_available === false &&
                        !orchestrate?.userid_available === false
                    ) {
                        console.info('Account Registration: Failed')

                        setLoading(false)
                        dispatch(
                            updateAccountValidity({
                                digital_name: digital_name,
                                errors: {
                                    Email: true,
                                    EmailErrMsg: 'Email Address Already In Use',
                                    UserID: true,
                                    UserIDErrMsg: 'User Id Already In Use',
                                },
                            })
                        )
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Registration Failed:',
                            subtitle:
                                'Account Email Address & User Id Already In Use',
                        })

                        console.log(
                            'Account Registration Failed: Account Email Address & User Id Already In Use'
                        )
                    } else {
                        console.info('Account Registration: Failed')

                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Registration Failed:',
                            subtitle:
                                'Submission Does Not Meet Account Creation Requirements',
                        })

                        console.log(
                            'Account Registration Failed: Submission Does Not Meet Account Creation Requirements'
                        )
                    }
                } else {
                    console.info('Account Registration: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Registration Failed:',
                        subtitle:
                            'Please Fix All Validation Errors Before Trying To Resubmit',
                    })

                    console.log(
                        'Account Registration Invalid: Please Fix All Validation Errors Before Trying To Resubmit'
                    )
                }
            } else {
                console.info('Account Registration: Failed')

                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Account Registration Failed:',
                    subtitle:
                        'Please Check The Security Measures Box Before Trying To Resubmit',
                })

                console.log(
                    'Account Registration Invalid: Please Check The Security Measures Box Before Trying To Resubmit'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Modal Submit (Method)
    const handleModalSubmit = async () => {
        try {
            // Navigate To Next Form
            navigate(submit_route ? submit_route : '/')

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Submit Error:', error)
            return null
        }
    }

    // Handle Modal Close (Method)
    const handleModalClose = async () => {
        try {
            setModalOpen(false)

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Close Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Key Down Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <div className="form" data-testid="account-registration-page">
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ''}
                    </h2>
                    <LoadingComponent />
                </div>
            </>
        )
    } else if (accountData?.[digital_name]?.auxiliary?.checked) {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-registration-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ''}
                    </h2>
                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Owner" />

                        <TextInput
                            className="required"
                            labelText="First Name:"
                            id="FirstName"
                            name="FirstName"
                            autocomplete="name"
                            placeholder="First Name"
                            value={
                                accountData?.[digital_name]?.values?.FirstName
                            }
                            maxLength="16"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.FirstName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.FirstNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="First Name"
                            aria-label="FirstName"
                        />

                        <TextInput
                            labelText="Middle Name:"
                            id="MiddleName"
                            name="MiddleName"
                            autocomplete="additional-name"
                            placeholder="Middle Name"
                            value={
                                accountData?.[digital_name]?.values?.MiddleName
                            }
                            maxLength="40"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.MiddleName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.MiddleNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Middle Name"
                            aria-label="MiddleName"
                        />

                        <TextInput
                            className="required"
                            labelText="Last Name:"
                            id="LastName"
                            name="LastName"
                            autocomplete="family-name"
                            placeholder="Last Name"
                            value={
                                accountData?.[digital_name]?.values?.LastName
                            }
                            maxLength="40"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.LastName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.LastNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Middle Name"
                            aria-label="MiddleName"
                        />

                        <DividerComponent text="Account Login" />

                        <TooltipComponent
                            text="Email Format Guidance"
                            tooltip="(e.g. written_name@domain.com)"
                        />

                        <TextInput
                            className="required"
                            labelText="Email:"
                            id="Email"
                            name="Email"
                            autocomplete="email"
                            placeholder="email@email.com"
                            value={accountData?.[digital_name]?.values?.Email}
                            maxLength="50"
                            type="email"
                            onChange={handleData}
                            invalid={accountData?.[digital_name]?.errors?.Email}
                            invalidText={
                                accountData?.[digital_name]?.errors?.EmailErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="email@email.com"
                            aria-label="Email"
                        />

                        <TextInput
                            className="required"
                            labelText="Confirm Email:"
                            id="EmailConfirm"
                            name="EmailConfirm"
                            autocomplete="email"
                            placeholder="email@email.com"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.EmailConfirm
                            }
                            maxLength="50"
                            type="email"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.EmailConfirm
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.EmailConfirmErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="email@email.com"
                            aria-label="EmailConfirm"
                        />

                        <TooltipComponent
                            text="User ID Notice Guidance"
                            tooltip="If you would like to use your email address as your User ID, select this box."
                        />

                        <CheckUserIDComponent />

                        <DividerComponent text="Password Recovery" />

                        <Select
                            className="required"
                            defaultValue={
                                accountData?.[digital_name]?.values
                                    ?.SecretQuestion
                            }
                            value={
                                accountData?.[digital_name]?.values
                                    ?.SecretQuestion
                            }
                            helper=""
                            labelText="Secret Question:"
                            id="SecretQuestion"
                            name="SecretQuestion"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.SecretQuestion
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.SecretQuestionErrMsg
                            }
                            role="listbox"
                            tabIndex="0"
                            aria-label="SecretQuestion"
                        >
                            <SelectItem text="Choose an option" value="" />
                            <SelectItem
                                text="What is your paternal grandmother's first name?"
                                value="1031"
                                role="option"
                            />
                            <SelectItem
                                text="What is your maternal grandmother's first name?"
                                value="1040"
                                role="option"
                            />
                            <SelectItem
                                text="What was the color of your first car?"
                                value="1041"
                                role="option"
                            />
                            <SelectItem
                                text="What is the name of your childhood best friend?"
                                value="1042"
                                role="option"
                            />
                            <SelectItem
                                text="What city were you born?"
                                value="1043"
                                role="option"
                            />
                            <SelectItem
                                text="What city did you meet your significant other?"
                                value="1044"
                                role="option"
                            />
                            <SelectItem
                                text="What was your childhood nickname?"
                                value="1045"
                                role="option"
                            />
                            <SelectItem
                                text="What is your oldest sibling's middle name?"
                                value="1046"
                                role="option"
                            />
                            <SelectItem
                                text="In what city or town was your first job?"
                                value="1047"
                                role="option"
                            />
                            <SelectItem
                                text="What school did you attend in the sixth grade?"
                                value="1048"
                                role="option"
                            />
                            <SelectItem
                                text="What is the middle name of your oldest child?"
                                value="1050"
                                role="option"
                            />
                        </Select>

                        <TextInput
                            className="required"
                            labelText="Secret Answer:"
                            id="SecretAnswer"
                            name="SecretAnswer"
                            placeholder="Secret Answer"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.SecretAnswer
                            }
                            maxLength="100"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswer
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswerErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Secret Answer"
                            aria-label="SecretAnswer"
                        />

                        <DividerComponent text="Communication" />

                        <RadioButtonGroup
                            className={
                                accountData?.[digital_name]?.errors?.EmailComm
                                    ? 'required invalid'
                                    : 'required'
                            }
                            defaultSelected={
                                accountData?.[digital_name]?.values?.EmailComm
                            }
                            valueSelected={
                                accountData?.[digital_name]?.values?.EmailComm
                            }
                            legendText="Email Communication:"
                            name="EmailComm"
                            invalid={
                                accountData?.[digital_name]?.errors?.EmailComm
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.EmailCommErrMsg
                            }
                            role="radiogroup"
                            aria-label="EmailComm"
                        >
                            <RadioButton
                                id="EmailCommTrue"
                                name="EmailCommTrue"
                                labelText="Yes"
                                value="Y"
                                type="radio"
                                onClick={handleData}
                            />
                            <RadioButton
                                id="EmailCommFalse"
                                name="EmailCommFalse"
                                labelText="No"
                                value="N"
                                type="radio"
                                onClick={handleData}
                            />
                        </RadioButtonGroup>

                        <DividerComponent text="Security Measures" />

                        <ReCAPTCHA
                            sitekey={
                                process.env.REACT_APP_GOOGLE_RE_CAPTCHA_SITE_KEY
                            }
                            onChange={handleReCaptcha}
                        />

                        <ModalComponent
                            modal={modalData}
                            open={modalOpen}
                            handleModalClose={handleModalClose}
                            handleModalSubmit={handleModalSubmit}
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                    tabIndex="0"
                                    role="button"
                                    aria-pressed="false"
                                    aria-label="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-registration-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    } else {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-registration-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ''}
                    </h2>
                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Owner" />

                        <TextInput
                            className="required"
                            labelText="First Name:"
                            id="FirstName"
                            name="FirstName"
                            autocomplete="name"
                            placeholder="First Name"
                            value={
                                accountData?.[digital_name]?.values?.FirstName
                            }
                            maxLength="16"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.FirstName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.FirstNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="First Name"
                            aria-label="FirstName"
                        />

                        <TextInput
                            labelText="Middle Name:"
                            id="MiddleName"
                            name="MiddleName"
                            autocomplete="additional-name"
                            placeholder="Middle Name"
                            value={
                                accountData?.[digital_name]?.values?.MiddleName
                            }
                            maxLength="40"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.MiddleName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.MiddleNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Middle Name"
                            aria-label="MiddleName"
                        />

                        <TextInput
                            className="required"
                            labelText="Last Name:"
                            id="LastName"
                            name="LastName"
                            autocomplete="family-name"
                            placeholder="Last Name"
                            value={
                                accountData?.[digital_name]?.values?.LastName
                            }
                            maxLength="40"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.LastName
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.LastNameErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Middle Name"
                            aria-label="MiddleName"
                        />

                        <DividerComponent text="Account Login" />

                        <TooltipComponent
                            text="Email Format Guidance"
                            tooltip="(e.g. written_name@domain.com)"
                        />

                        <TextInput
                            className="required"
                            labelText="Email:"
                            id="Email"
                            name="Email"
                            autocomplete="email"
                            placeholder="email@email.com"
                            value={accountData?.[digital_name]?.values?.Email}
                            maxLength="50"
                            type="email"
                            onChange={handleData}
                            invalid={accountData?.[digital_name]?.errors?.Email}
                            invalidText={
                                accountData?.[digital_name]?.errors?.EmailErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="email@email.com"
                            aria-label="Email"
                        />

                        <TextInput
                            className="required"
                            labelText="Confirm Email:"
                            id="EmailConfirm"
                            name="EmailConfirm"
                            autocomplete="email"
                            placeholder="email@email.com"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.EmailConfirm
                            }
                            maxLength="50"
                            type="email"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.EmailConfirm
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.EmailConfirmErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="email@email.com"
                            aria-label="EmailConfirm"
                        />

                        <TooltipComponent
                            text="User ID Notice Guidance"
                            tooltip="If you would like to use your email address as your User ID, select this box."
                        />

                        <CheckUserIDComponent />

                        <TextInput
                            className="required"
                            labelText="User Id:"
                            id="UserID"
                            name="UserID"
                            placeholder="User ID"
                            value={
                                accountData?.[digital_name]?.values?.UserID
                                    ? accountData?.[digital_name]?.values
                                          ?.UserID
                                    : ''
                            }
                            maxLength="50"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.UserID
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.UserIDErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="User ID"
                            aria-label="UserID"
                        />

                        <DividerComponent text="Password Recovery" />

                        <Select
                            className="required"
                            defaultValue={
                                accountData?.[digital_name]?.values
                                    ?.SecretQuestion
                            }
                            value={
                                accountData?.[digital_name]?.values
                                    ?.SecretQuestion
                            }
                            helper=""
                            labelText="Secret Question:"
                            id="SecretQuestion"
                            name="SecretQuestion"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.SecretQuestion
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.SecretQuestionErrMsg
                            }
                            role="listbox"
                            tabIndex="0"
                            aria-label="SecretQuestion"
                        >
                            <SelectItem text="Choose an option" value="" />
                            <SelectItem
                                text="What is your paternal grandmother's first name?"
                                value="1031"
                                role="option"
                            />
                            <SelectItem
                                text="What is your maternal grandmother's first name?"
                                value="1040"
                                role="option"
                            />
                            <SelectItem
                                text="What was the color of your first car?"
                                value="1041"
                                role="option"
                            />
                            <SelectItem
                                text="What is the name of your childhood best friend?"
                                value="1042"
                                role="option"
                            />
                            <SelectItem
                                text="What city were you born?"
                                value="1043"
                                role="option"
                            />
                            <SelectItem
                                text="What city did you meet your significant other?"
                                value="1044"
                                role="option"
                            />
                            <SelectItem
                                text="What was your childhood nickname?"
                                value="1045"
                                role="option"
                            />
                            <SelectItem
                                text="What is your oldest sibling's middle name?"
                                value="1046"
                                role="option"
                            />
                            <SelectItem
                                text="In what city or town was your first job?"
                                value="1047"
                                role="option"
                            />
                            <SelectItem
                                text="What school did you attend in the sixth grade?"
                                value="1048"
                                role="option"
                            />
                            <SelectItem
                                text="What is the middle name of your oldest child?"
                                value="1050"
                                role="option"
                            />
                        </Select>

                        <TextInput
                            className="required"
                            labelText="Secret Answer:"
                            id="SecretAnswer"
                            name="SecretAnswer"
                            placeholder="Secret Answer"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.SecretAnswer
                            }
                            maxLength="100"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswer
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswerErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Secret Answer"
                            aria-label="SecretAnswer"
                        />

                        <DividerComponent text="Communication" />

                        <RadioButtonGroup
                            className={
                                accountData?.[digital_name]?.errors?.EmailComm
                                    ? 'required invalid'
                                    : 'required'
                            }
                            defaultSelected={
                                accountData?.[digital_name]?.values?.EmailComm
                            }
                            valueSelected={
                                accountData?.[digital_name]?.values?.EmailComm
                            }
                            legendText="Email Communication:"
                            name="EmailComm"
                            invalid={
                                accountData?.[digital_name]?.errors?.EmailComm
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.EmailCommErrMsg
                            }
                            role="radiogroup"
                            aria-label="EmailComm"
                        >
                            <RadioButton
                                id="EmailCommTrue"
                                name="EmailCommTrue"
                                labelText="Yes"
                                value="Y"
                                type="radio"
                                onClick={handleData}
                            />
                            <RadioButton
                                id="EmailCommFalse"
                                name="EmailCommFalse"
                                labelText="No"
                                value="N"
                                type="radio"
                                onClick={handleData}
                            />
                        </RadioButtonGroup>

                        <DividerComponent text="Security Measures" />

                        <ReCAPTCHA
                            sitekey={
                                process.env.REACT_APP_GOOGLE_RE_CAPTCHA_SITE_KEY
                            }
                            onChange={handleReCaptcha}
                        />

                        <ModalComponent
                            modal={modalData}
                            open={modalOpen}
                            handleModalClose={handleModalClose}
                            handleModalSubmit={handleModalSubmit}
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                    tabIndex="0"
                                    role="button"
                                    aria-pressed="false"
                                    aria-label="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-registration-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    }
}

export default RegistrationPage
